import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { SurveyorService } from 'src/app/services/SurveyorService/surveyor.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-bad-feedback',
    templateUrl: './bad-feedback.component.html',
    styleUrls: ['./bad-feedback.component.css'],
    standalone: false
})
export class BadFeedbackComponent implements OnInit, OnDestroy {
  reasons: string[] = [
    'Uncooperative Behaviour',
    'Incomplete Information',
  ];
  selectionControl: FormControl = new FormControl([]);
  commentControl: FormControl = new FormControl('');
  previewFeedbackControl: FormControl = new FormControl('');
  badFeedback: boolean = false;
  textDisplay = 'We are sorry to hear about your experience with us.';
  urlParams: any = {
    claimID: '',
    requestID: '',
    isGoodFeedback: 0,
    encClaimID: ''
  };
  feedbackData: any;
  feedbackText = 'Tell us more about what did you like the most?';
  feedbackTextPlaceholder = '';
  templateData: any = '';
  showRight: boolean = false;
  // Cached mobile detection for template bindings
  isMobileView: boolean = false;
  // Good feedback flow states
  currentStep: 'compose' | 'preview' = 'compose';
  selectedPlatformId: '1' | '3' | null = null; // 1: LinkedIn, 3: Twitter/X
  selectedPlatformName: 'linkedin' | 'twitter' | '' = '';
  socialDisableReason: string = '';
  shareValid: boolean = false;
  needsPreOpen: boolean = false;
  twitterUrlToOpen: string | null = null;

  // Track if we pushed a history state when entering preview
  private previewStatePushed: boolean = false;
  // Teardown notifier for subscriptions
  private destroy$ = new Subject<void>();

  constructor(private activatedRouter: ActivatedRoute, private surveyorService: SurveyorService, private snackBar: MatSnackBar) { }

  ngOnInit(): void {
    // Route params and flags
    try {
      const encodedClaim = this.activatedRouter.snapshot.paramMap.get('claimID');
      this.urlParams.claimID = encodedClaim ? atob(encodedClaim) : '';
    } catch { this.urlParams.claimID = ''; }
    this.urlParams.isFeedbackType = this.urlParams.claimID.split(':')[this.urlParams.claimID.split(':').length - 1];
    this.badFeedback = this.urlParams.isFeedbackType == 1 ? false : true;
    this.urlParams.encClaimID = this.urlParams.claimID.split(':')[0];
    try {
      const encodedRequest = this.activatedRouter.snapshot.paramMap.get('requestID');
      this.urlParams.requestID = encodedRequest ? atob(encodedRequest) : null;
    } catch { this.urlParams.requestID = null; }

    // Restore persisted state if present and start persisting changes
    this.restorePersistedState();
    this.subscribeToFormChangesOnce();
    this.changeMessage();
    this.initFeedbackSubmit(true);

    // Cache mobile flag to avoid calling a function from template repeatedly
    this.isMobileView = this.isMobile();
    this.needsPreOpen = this.getDeviceType().toLowerCase() == 'ios' || this.isSafariBrowser();
    // Initialize share state at startup
    this.computeShareValidity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Keep cached view in sync on resize without calling function from template
    this.isMobileView = this.isMobile();
  }

  // Handle hardware/browser back: when on preview, return to compose instead of leaving
  @HostListener('window:popstate')
  onPopState(): void {
    if (this.previewStatePushed && this.currentStep === 'preview') {
      // We had pushed a state when entering preview; popping it should bring us back to compose
      this.previewStatePushed = false;
      this.goBack();
    }
  }

  private isSafariBrowser(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android");
  }

  // ---- Persistence helpers ----
  private getLocalStorageKeySuffix(): string {
    // Scope cache to claim and request to avoid leaking across sessions
    const encClaim = this.urlParams?.encClaimID || '';
    const requestId = this.urlParams?.requestID || '';
    return `${encClaim}::${requestId}`;
  }

  private persistState(): void {
    try {
      const keySuffix = this.getLocalStorageKeySuffix();
      const payload = {
        currentStep: this.currentStep,
        selectedPlatformId: this.selectedPlatformId,
        comment: this.commentControl?.value ?? '',
        preview: this.previewFeedbackControl?.value ?? '',
        selectedPlatformName: this.selectedPlatformName,
      };
      localStorage.setItem(`bf_state_${keySuffix}`, JSON.stringify(payload));
    } catch { }
  }

  private restorePersistedState(): void {
    try {
      const keySuffix = this.getLocalStorageKeySuffix();
      const raw = localStorage.getItem(`bf_state_${keySuffix}`);
      if (!raw) { return; }
      const state = JSON.parse(raw || '{}');

      if (state?.comment != null) {
        this.commentControl.patchValue(state.comment);
      }
      if (state?.preview != null) {
        this.previewFeedbackControl.patchValue(state.preview);
      }
      if (state?.selectedPlatformId === '1' || state?.selectedPlatformId === '3') {
        this.selectedPlatformId = state.selectedPlatformId;
      }
      if (state?.currentStep === 'compose' || state?.currentStep === 'preview') {
        this.currentStep = state.currentStep;
      }
      if (state?.selectedPlatformName === 'linkedin' || state?.selectedPlatformName === 'twitter') {
        this.selectedPlatformName = state.selectedPlatformName;
      }
    } catch { }
  }

  private subscribeToFormChangesOnce(): void {
    // Persist on changes (debounced) and recompute share validity
    this.commentControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.persistState();
        this.computeShareValidity();
      });

    this.previewFeedbackControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.persistState());
  }

  selectReason(event: any, reason: string, index: number) {
    const currentValues: any[] = Array.isArray(this.selectionControl.value) ? [...this.selectionControl.value] : [];
    if (event?.target?.checked) {
      if (!currentValues.includes(reason)) {
        currentValues.push(reason);
      }
      this.selectionControl.patchValue(currentValues);
    } else {
      currentValues.splice(index, 1);
      this.selectionControl.patchValue(currentValues);
    }
  }

  trackByIndex(index: number): number { return index; }

  changeMessage() {
    let platformName = '';
    if (this.urlParams.isFeedbackType == 1) {
      this.textDisplay = 'Thank you! We look forward to serving you again.';
      if (this.feedbackData?.FeedbackType == 'HealthClaimsFeedback') {
        this.feedbackText = 'Tell us more about what did you like the most?'
        this.feedbackTextPlaceholder = 'Write a few words that you liked about the claim experience… (write minimum 2 lines)';
      }
      else {
        if (this.currentStep === 'compose') {
          this.feedbackText = this.feedbackData?.AgentEmployeeId && this.feedbackData?.AgentName ? 'Take a moment to appreciate our claims advisor ' + this.feedbackData?.AgentName + '(' + this.feedbackData?.AgentEmployeeId + ')' : 'Take a moment to appreciate our claims advisor';
          this.feedbackTextPlaceholder = 'Write a few words that you liked about the claim experience… (write minimum 2 lines)';
        }
        else {
          platformName = this.selectedPlatformName === 'linkedin' ? 'linkedIn' : 'twitter';
          this.feedbackText = 'We’ve refined your appreciation note. Here’s how it will look when posted on ' + platformName;
          this.feedbackTextPlaceholder = '';
        }
      }
    }
    else {
      this.textDisplay = 'We are sorry to hear about your experience with us.'
      this.feedbackText = 'What went wrong in our assistance?'
    }
  }

  initFeedbackSubmit(init = false) {
    let payload = {
      "AgentRating": this.urlParams.isFeedbackType,  // 1 For Good and 3 For Bad
      "IsSubmit": true,
      "CreatedBy": 124,
      "EncClaimID": this.urlParams.encClaimID,
      "FeedbackRequestId": this.urlParams.requestID || null,
      "IsV2Version": this.urlParams.requestID ? true : false,
      "FeedbackQuesAns": [
        {
          "Question": "How was your experience?",
          "InputType": "Feedback",
          "Response": this.urlParams.isFeedbackType == 1 ? 'Good' : 'Bad'
        }
      ],
    }
    if (this.urlParams.isFeedbackType == 1 && !init) {
      let pushObj = {
        "Question": "Comments",
        "InputType": "Text",
        "Response": this.commentControl.value
      }
      payload.FeedbackQuesAns.push(pushObj);
    }
    else if (this.urlParams.isFeedbackType == 3 && !init) {
      let pushObjReasons = {
        "Question": "What went wrong in our assistance?",
        "InputType": "Selection",
        "Response": (this.selectionControl.value || []).toString()
      }
      payload.FeedbackQuesAns.push(pushObjReasons);
      let pushObjComments = {
        "Question": "Comments",
        "InputType": "Text",
        "Response": this.commentControl.value
      }
      payload.FeedbackQuesAns.push(pushObjComments);
    }

    this.surveyorService.setCustomerFeedback(payload).subscribe((data: any) => {
      if (data.Data != null) {
        if ((data?.Data?.FeedbackType == 'ClaimsFeedbackPB' || data?.Data?.FeedbackType == 'HealthClaimsFeedback') && data?.Data?.ProductId != 114) {
          if (!this.badFeedback) {
            if (data?.Data?.FeedbackType == 'HealthClaimsFeedback')
              this.getCustomerFeedbackTemplate();
            this.feedbackData = data?.Data;
            this.changeMessage();
            this.showRight = true;
            const leftPanel = document.getElementById('leftPanel');
            if (leftPanel && (leftPanel as any).style) {
              (leftPanel as any).style.animationPlayState = 'running';
            }
          } else {
            this.feedbackData = {};
          }
        }

        //if (init) {
        //  this.getCustomerFeedback();
        //}

        //if (!init) {
        //  this.feedbackData.FeedbackType = '';
        //}
      }
      else {
        this.changeMessage();
      }
    })
  }

  // ---- Share flow helpers (Good feedback) ----
  private computeShareValidity(): void {
    const text = (this.commentControl?.value || '').toString();
    const textLength = text.replace(/\s+/g, ' ').trim().length;
    if (textLength < 80) {
      this.socialDisableReason = "We’d love to hear a bit more! Please share at least 80 characters so we can craft a meaningful appreciation for your claims advisor.";
      this.shareValid = false;
    } else if (textLength > 750) {
      this.socialDisableReason = "Whoa! That’s a lot to say 😊. Please keep it under 750 characters so your appreciation can be shared smoothly on social media.";
      this.shareValid = false;
    } else {
      this.socialDisableReason = '';
      this.shareValid = true;
    }
  }

  canShare(): boolean { return this.shareValid; }

  openPreview(event: Event, platformId: '1' | '3') {
    if (!this.shareValid) {
      event.preventDefault();
      this.snackBar.open(this.socialDisableReason, null, {
        duration: 10000,
        verticalPosition: 'bottom' // or 'top'
      });
    } else {
      if (this.feedbackData?.FeedbackType == 'ClaimsFeedbackPB') {
        // Try cached preview first
        const cached = this.tryGetCachedPreview(platformId);
        if (cached) {
          this.templateData = cached.templateData;
          this.selectedPlatformId = platformId;
          this.selectedPlatformName = platformId === '3' ? 'twitter' : 'linkedin';
          this.currentStep = 'preview';
          this.previewFeedbackControl.patchValue(cached.templateData?.TemplateDescription || '');
          this.changeMessage();
          this.persistState();
          // Push a history state so device back on mobile returns to compose
          this.pushPreviewHistoryState();
          return;
        }
        this.getCustomerFeedbackPostPreview(platformId);
      }
      else if (platformId) {
        let preOpened: Window | null = null;
        if (this.needsPreOpen) {
          try { preOpened = window.open('', '_blank'); } catch { preOpened = null; }
        }
        this.sendPost(platformId, preOpened);
      }
    }
  }

  postFromPreview() {
    if (this.selectedPlatformId) {
      // Pre-open window in direct click handler to avoid mobile popup blockers
      let preOpened: Window | null = null;
      if (this.needsPreOpen) {
        try { preOpened = window.open('', '_blank'); } catch { preOpened = null; }
      }
      this.sendPost(this.selectedPlatformId, preOpened);
    }
  }

  private pushPreviewHistoryState(): void {
    try {
      // Only push on mobile devices to handle hardware back behavior
      if (!this.previewStatePushed && this.isMobile()) {
        window.history.pushState({ preview: true }, '');
        this.previewStatePushed = true;
      }
    } catch { }
  }

  goBack() {
    this.currentStep = 'compose';
    this.changeMessage();
    this.persistState();
    // If we had pushed a history state for preview, pop it so history stays clean
    try {
      if (this.previewStatePushed) {
        this.previewStatePushed = false;
        window.history.back();
      }
    } catch { }
  }

  //getCustomerFeedback() {
  //  this.surveyorService.getCustomerFeedback(btoa(this.urlParams.encClaimID)).subscribe((data: any) => {
  //    this.feedbackData = data.Data[0];
  //  })
  //}

  getCustomerFeedbackTemplate() {
    this.surveyorService.getCustomerFeedbackTemplate(btoa(this.urlParams.encClaimID)).subscribe((data: any) => {
      if (data?.Data) {
        this.templateData = data.Data;
        this.commentControl.patchValue(this.templateData?.TemplateDescription || '')
        this.persistState();
      }
    })
  }

  sendPost(platformId: '1' | '3', preOpenedWindow?: Window | null) {
    if (!this.templateData) {
      let cached = this.tryGetCachedPreview(platformId);
      if (cached?.templateData) {
        this.templateData = cached?.templateData;
      }
      else {
        this.onBackClick();
        return;
      }
    }
    let payload = {
      "enctxt": this.templateData?.TemplateName,
      "socialId": platformId,
      "Description": this.feedbackData?.FeedbackType == 'HealthClaimsFeedback' ? this.commentControl.value : this.previewFeedbackControl?.value,
      "DeviceType": this.getDeviceType() || '',
      "FeedbackType": this.feedbackData?.FeedbackType || '',
      "TemplateId" :this.templateData?.TemplateId,
    }
    
    this.surveyorService.sendAppreciationPost(payload).subscribe((data: any) => {
      if (data?.ErrorCode == 0 && data?.Data) {
        if (platformId == '1' || this.feedbackData?.FeedbackType == 'HealthClaimsFeedback' || this.getDeviceType().toLowerCase() != 'ios') {
          if (preOpenedWindow && !preOpenedWindow.closed) {
            try { preOpenedWindow.location.href = data.Data; } catch { window.open(data.Data, "_blank"); }
          } else {
            if (platformId == '3' && this.getDeviceType().toLowerCase == 'android') {
              this.twitterUrlToOpen = data?.Data;
              if (confirm("Tap OK to open Twitter app")) {
                this.openTwitter();
              } else {
                this.snackBar.open("Twitter post canceled.", null, { duration: 3000 });
              }
            }
            else
              window.open(data.Data, "_blank");
          }
        } else {
          this.openTwitterWithFallback(data.Data, preOpenedWindow);
        }
      }
      else {
        if (preOpenedWindow && !preOpenedWindow.closed) {
          try { preOpenedWindow.close(); } catch { }
        }
        this.snackBar.open(data?.Error || "Something went wrong. Please try again later.", null, {
          duration: 10000,
          panelClass: ['error-snack'],
        });
      }
    })
  }

  onBackClick(): void {
    if (this.currentStep === 'preview') {
      this.goBack();
      return;
    }
    try {
      if (window.history.length > 1) { window.history.back(); }
    } catch { }
  }

  private getDeviceType() {
    let deviceType;
    const userAgent = navigator.userAgent;

    if (/android/i.test(userAgent)) {
      deviceType = "Android";
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      deviceType = "iOS";
    } else {
      deviceType = "Desktop";
    }

    return deviceType;
  }

  private isMobile() {
    var check = false;
    (function (a, b) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)\n|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)\n|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent);
    return check;
  }

  getCustomerFeedbackPostPreview(platformId: '1' | '3') {
    let payload = {
      "EncClaimId": this.urlParams.encClaimID || '',
      "SocialId": platformId || 0,
      "FeedbackByCustomer": this.commentControl.value,
      "EncEmployeeId": this.feedbackData?.EncEmployeeId || '',
    }

    this.surveyorService.getCustomerFeedbackPostPreview(payload).subscribe((data: any) => {
      if (data?.ErrorCode == 0 && data?.Data) {
        this.templateData = data?.Data;
        this.selectedPlatformId = platformId;
        this.selectedPlatformName = platformId === '3' ? 'twitter' : 'linkedin';
        this.currentStep = 'preview';
        this.changeMessage();
        this.previewFeedbackControl.patchValue(data?.Data?.TemplateDescription || '');
        // Save preview into cache and persist state
        this.savePreviewToCache(platformId, this.commentControl?.value || '', data?.Data);
        this.persistState();
        // Push a history state so device back on mobile returns to compose
        this.pushPreviewHistoryState();
      }
      else {
        this.currentStep = 'compose';
        this.snackBar.open(data?.Error || "Something went wrong. Please try again later.", null, {
          duration: 10000,
          panelClass: ['error-snack'],
        });
      }
    })
  }

  // ---- Preview caching ----
  private getPreviewCacheKey(platformId: '1' | '3', inputText: string): string {
    const keySuffix = this.getLocalStorageKeySuffix();
    const normalized = (inputText || '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const employee = this.feedbackData?.EncEmployeeId || '';
    return `bf_preview_${keySuffix}::${platformId}::${employee}::${this.hashString(normalized)}`;
  }

  private hashString(text: string): number {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const chr = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  private savePreviewToCache(platformId: '1' | '3', inputText: string, templateData: any): void {
    try {
      const key = this.getPreviewCacheKey(platformId, inputText || '');
      const payload = { templateData, savedAt: Date.now() };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch { }
  }

  private tryGetCachedPreview(platformId: '1' | '3'): { templateData: any } | null {
    try {
      const key = this.getPreviewCacheKey(platformId, this.commentControl?.value || '');
      const raw = localStorage.getItem(key);
      if (!raw) { return null; }
      const parsed = JSON.parse(raw);
      // TTL: 24 hours
      const ttlMs = 24 * 60 * 60 * 1000;
      if (!parsed?.savedAt || (Date.now() - parsed.savedAt) > ttlMs) {
        localStorage.removeItem(key);
        return null;
      }
      return { templateData: parsed.templateData };
    } catch {
      return null;
    }
  }

  private openTwitterWithFallback(appUrl: string, preOpenedWindow?: Window | null) {
    var webUrl = appUrl.replace('twitter://post?message=', 'https://twitter.com/intent/tweet?text=');
    //webUrl = appUrl.replace('#Intent;package=com.twitter.android;scheme=twitter;end', '');
    //webUrl = appUrl.replace('intent://post?message=', 'https://twitter.com/intent/tweet?text=');

    if (preOpenedWindow && !preOpenedWindow.closed) {
      try {
        preOpenedWindow.location.href = appUrl; // Try deep link
      } catch {
        preOpenedWindow.location.href = webUrl; // fallback if fails immediately
      }

      // fallback after delay if app never opens
      setTimeout(() => {
        if (preOpenedWindow && !preOpenedWindow.closed) {
          try { preOpenedWindow.location.href = webUrl; } catch { }
        }
      }, 800);

    } else {
      // No pre-open: open app URL (may fail)
      let newWindow: Window | null = null;
      try { newWindow = window.open(appUrl, "_blank"); } catch { newWindow = null; }

      // fallback to web intent
      setTimeout(() => {
        if (!newWindow || newWindow.closed) {
          window.open(webUrl, "_blank");
        }
      }, 800);
    }
  }

  openTwitter() {
    if (this.twitterUrlToOpen) {
      const a = document.createElement('a');
      a.href = this.twitterUrlToOpen;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }
}

